import type { AuditEventText, AuditEventValue } from './types';

export enum AuditEventCode {
  CreatePatient = 'auditevent.patient.create',
  UpdatePatient = 'auditevent.patient.update',
  CreateDeviceUseStatement = 'auditevent.deviceusestatement.create',
  UpdateDeviceUseStatement = 'auditevent.deviceusestatement.update',
  CreateObservation = 'auditevent.observation.create',
  UpdateObservation = 'auditevent.observation.update',
  CreatePractitioner = 'auditevent.practitioner.create',
  UpdatePractitioner = 'auditevent.practitioner.update',
  CreateDevice = 'auditevent.device.create',
  UpdateDevice = 'auditevent.device.update',
  CreateDeviceMetric = 'auditevent.devicemetric.create',
  UpdateDeviceMetric = 'auditevent.devicemetric.update',
  CreateEncounter = 'auditevent.encounter.create',
  UpdateEncounter = 'auditevent.encounter.update',
  DeleteEncounter = 'auditevent.encounter.delete',
  CreateGroup = 'auditevent.group.create',
  UpdateGroup = 'auditevent.group.update',
  CreateOrganization = 'auditevent.organization.create',
  UpdateOrganization = 'auditevent.organization.update',
  CreateDiagnosticReport = 'auditevent.diagnosticreport.create',
  UpdateDiagnosticReport = 'auditevent.diagnosticreport.update',
  CreateRelatedPerson = 'auditevent.relatedperson.create',
  UpdateRelatedPerson = 'auditevent.relatedperson.update',
  CreateCoverage = 'auditevent.coverage.create',
  UpdateCoverage = 'auditevent.coverage.update',
  CreateServiceRequest = 'auditevent.servicerequest.create',
  UpdateServiceRequest = 'auditevent.servicerequest.update',
  CreateMedia = 'auditevent.media.create',
  UpdateMedia = 'auditevent.media.update',
  CreateMedicationStatement = 'auditevent.medicationstatement.create',
  UpdateMedicationStatement = 'auditevent.medicationstatement.update',
  CreateClinicalImpression = 'auditevent.clinicalimpression.create',
  UpdateClinicalImpression = 'auditevent.clinicalimpression.update',
  CreateMedicationRequest = 'auditevent.medicationrequest.create',
  UpdateMedicationRequest = 'auditevent.medicationrequest.update',

  // PractitionerRole
  CreatePractitionerRole = 'auditevent.practitionerrole.create',

  CreateClaim = 'auditevent.claim.create',
  UpdateClaim = 'auditevent.claim.update',

  CreateClaimResponse = 'auditevent.claimresponse.create',
  UpdateClaimResponse = 'auditevent.claimresponse.update',

  CreateChargeItemDefinition = 'auditevent.chargeitemdefinition.create',
  UpdateChargeItemDefinition = 'auditevent.chargeitemdefinition.update',

  CreateChargeItem = 'auditevent.chargeitem.create',
  UpdateChargeItem = 'auditevent.chargeitem.update',

  CreateSupplyDelivery = 'auditevent.supplydelivery.create',
  UpdateSupplyDelivery = 'auditevent.supplydelivery.update',

  CreateTask = 'auditevent.task.create',
  UpdateTask = 'auditevent.task.update',

  CreateLocation = 'auditevent.location.create',
  UpdateLocation = 'auditevent.location.update',

  CreateCondition = 'auditevent.condition.create',
  UpdateCondition = 'auditevent.condition.update',

  CreateLibrary = 'auditevent.library.create',
  UpdateLibrary = 'auditevent.library.update',

  CreateRiskAssessment = 'auditevent.riskassessment.create',
  UpdateRiskAssessment = 'auditevent.riskassessment.update',

  CreateDocumentReference = 'auditevent.documentreference.create',
  UpdateDocumentReference = 'auditevent.documentreference.update',

  // Special cases
  EndDeviceUseStatement = 'auditevent.deviceusestatement.end',
  StartDeviceUseStatement = 'auditevent.deviceusestatement.start',
  CancelDeviceUseStatement = 'auditevent.deviceusestatement.cancel',
  EndObservation = 'auditevent.observation.end',
  StartObservation = 'auditevent.observation.start',
  CancelObservation = 'auditevent.observation.cancel',
  DischargePatient = 'auditevent.patient.discharge',
  ReadmitPatient = 'auditevent.patient.readmit',
  OnboardPatient = 'auditevent.patient.onboard',

  UpdateClaimTemplate = 'auditevent.claimtemplate.update',

  ResumeAdmission = 'auditevent.admission.resume',
  PauseAdmission = 'auditevent.admission.pause',
  InvalidateAdmission = 'auditevent.admission.invalidate',

  CreateAllergy = 'auditevent.allergyintolerance.create',
  ReadAllergy = 'auditevent.allergyintolerance.read',
  UpdateAllergy = 'auditevent.allergyintolerance.update',

  CreateCommunication = 'auditevent.communication.create',
  ReadCommunication = 'auditevent.communication.read',
  UpdateCommunication = 'auditevent.communication.update',

  CreateCareTeam = 'auditevent.careteam.create',
  UpdateCareTeam = 'auditevent.careteam.update',

  CreatePlanDefinition = 'auditevent.plandefinition.create',
  UpdatePlanDefinition = 'auditevent.plandefinition.update',

  CreateCarePlan = 'auditevent.careplan.create',
  UpdateCarePlan = 'auditevent.careplan.update',

  CreateActivityDefinition = 'auditevent.activitydefinition.create',
  UpdateActivityDefinition = 'auditevent.activitydefinition.update',

  CreateQuestionnaire = 'auditevent.questionnaire.create',
  UpdateQuestionnaire = 'auditevent.questionnaire.update',

  CreateQuestionnaireResponse = 'auditevent.questionnaireresponse.create',
  UpdateQuestionnaireResponse = 'auditevent.questionnaireresponse.update',

  CreateFlag = 'auditevent.flag.create',
  UpdateFlag = 'auditevent.flag.update',

  CreateSearchParameter = 'auditevent.searchparameter.create',
  UpdateSearchParameter = 'auditevent.searchparameter.update',
}

export const AuditEventDescription: Record<AuditEventValue, AuditEventText> = {
  [AuditEventCode.CreatePatient]: 'Create Patient',
  [AuditEventCode.UpdatePatient]: 'Update Patient',
  [AuditEventCode.CreatePractitioner]: 'Create Practitioner',
  [AuditEventCode.UpdatePractitioner]: 'Update Practitioner',
  [AuditEventCode.CreateDeviceUseStatement]: 'Create DeviceUseStatement',
  [AuditEventCode.UpdateDeviceUseStatement]: 'Update DeviceUseStatement',
  [AuditEventCode.CreateObservation]: 'Create Observation',
  [AuditEventCode.UpdateObservation]: 'Update Observation',
  [AuditEventCode.CreateDevice]: 'Create Device',
  [AuditEventCode.UpdateDevice]: 'Update Device',
  [AuditEventCode.CreateDeviceMetric]: 'Create DeviceMetric',
  [AuditEventCode.UpdateDeviceMetric]: 'Update DeviceMetric',
  [AuditEventCode.CreateEncounter]: 'Create Encounter',
  [AuditEventCode.UpdateEncounter]: 'Update Encounter',
  [AuditEventCode.CreateGroup]: 'Create Group',
  [AuditEventCode.UpdateGroup]: 'Update Group',
  [AuditEventCode.CreateOrganization]: 'Create Organization',
  [AuditEventCode.UpdateOrganization]: 'Update Organization',
  [AuditEventCode.CreateDiagnosticReport]: 'Create DiagnosticReport',
  [AuditEventCode.UpdateDiagnosticReport]: 'Update DiagnosticReport',
  [AuditEventCode.CreateRelatedPerson]: 'Create RelatedPerson',
  [AuditEventCode.UpdateRelatedPerson]: 'Update RelatedPerson',
  [AuditEventCode.CreateCoverage]: 'Create Coverage',
  [AuditEventCode.UpdateCoverage]: 'Update Coverage',
  [AuditEventCode.CreateServiceRequest]: 'Create ServiceRequest',
  [AuditEventCode.UpdateServiceRequest]: 'Update ServiceRequest',
  [AuditEventCode.CreateMedia]: 'Create Media',
  [AuditEventCode.UpdateMedia]: 'Update Media',
  [AuditEventCode.CreateMedicationStatement]: 'Create MedicationStatement',
  [AuditEventCode.UpdateMedicationStatement]: 'Update MedicationStatement',
  [AuditEventCode.CreateClinicalImpression]: 'Create ClinicalImpression',
  [AuditEventCode.UpdateClinicalImpression]: 'Update ClinicalImpression',
  [AuditEventCode.CreateMedicationRequest]: 'Create MedicationRequest',
  [AuditEventCode.UpdateMedicationRequest]: 'Update MedicationRequest',

  [AuditEventCode.CreateClaimResponse]: 'Create ClaimResponse',
  [AuditEventCode.UpdateClaimResponse]: 'Update ClaimResponse',

  [AuditEventCode.CreateClaim]: 'Create Claim',
  [AuditEventCode.UpdateClaim]: 'Update Claim',

  [AuditEventCode.CreateTask]: 'Create Task',
  [AuditEventCode.UpdateTask]: 'Update Task',

  [AuditEventCode.CreateChargeItemDefinition]: 'Create ChargeItemDefinition',
  [AuditEventCode.UpdateChargeItemDefinition]: 'Update ChargeItemDefinition',

  [AuditEventCode.CreateChargeItem]: 'Create ChargeItem',
  [AuditEventCode.UpdateChargeItem]: 'Update ChargeItem',

  [AuditEventCode.CreateLocation]: 'Create Location',
  [AuditEventCode.UpdateLocation]: 'Update Location',

  [AuditEventCode.CreateCondition]: 'Create Condition',
  [AuditEventCode.UpdateCondition]: 'Update Condition',

  [AuditEventCode.CreateLibrary]: 'Create Library',
  [AuditEventCode.UpdateLibrary]: 'Update Library',

  [AuditEventCode.CreateDocumentReference]: 'Create DocumentReference',
  [AuditEventCode.UpdateDocumentReference]: 'Update DocumentReference',

  // Special cases
  [AuditEventCode.EndDeviceUseStatement]: 'Terminate DeviceUseStatement',
  [AuditEventCode.StartDeviceUseStatement]: 'Start DeviceUseStatement',
  [AuditEventCode.CancelDeviceUseStatement]: 'Cancel DeviceUseStatement',
  [AuditEventCode.EndObservation]: 'Terminate Observation',
  [AuditEventCode.StartObservation]: 'Start Observation',
  [AuditEventCode.CancelObservation]: 'Cancel Observation',
  [AuditEventCode.DischargePatient]: 'Discharge Patient',
  [AuditEventCode.ReadmitPatient]: 'Readmit Patient',
  [AuditEventCode.UpdateClaimTemplate]: 'Update Claim Template',
  [AuditEventCode.ResumeAdmission]: 'Resume Admission',
  [AuditEventCode.PauseAdmission]: 'Pause Admission',

  [AuditEventCode.UpdateAllergy]: 'Update AllergyIntolerance',
  [AuditEventCode.CreateAllergy]: 'Create AllergyIntolerance',

  [AuditEventCode.CreateCommunication]: 'Create Communication',

  [AuditEventCode.CreateCarePlan]: 'Create CarePlan',
  [AuditEventCode.UpdateCarePlan]: 'Update CarePlan',

  [AuditEventCode.CreateCareTeam]: 'Create CareTeam',
  [AuditEventCode.UpdateCareTeam]: 'Update CareTeam',

  [AuditEventCode.CreatePlanDefinition]: 'Create PlanDefinition',
  [AuditEventCode.UpdatePlanDefinition]: 'Update PlanDefinition',

  [AuditEventCode.CreateActivityDefinition]: 'Create ActivityDefinition',
  [AuditEventCode.UpdateActivityDefinition]: 'Update ActivityDefinition',

  [AuditEventCode.CreateQuestionnaire]: 'Create Questionnaire',
  [AuditEventCode.UpdateQuestionnaire]: 'Update Questionnaire',

  [AuditEventCode.CreateQuestionnaireResponse]: 'Create QuestionnaireResponse',
  [AuditEventCode.UpdateQuestionnaireResponse]: 'Update QuestionnaireResponse',

  [AuditEventCode.CreateRiskAssessment]: 'Create RiskAssessment',
  [AuditEventCode.UpdateRiskAssessment]: 'Update RiskAssessment',

  [AuditEventCode.CreateFlag]: 'Create Flag',
  [AuditEventCode.UpdateFlag]: 'Update Flag',
};
